import React, { useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useParams, useNavigate } from 'react-router-dom';
import useLocalStorage from '../../shared/hooks/useLocalStorage';
import storageService from '../../shared/storage/storageService';
import { autoMergeContent } from '../../shared/engine/contentMerger';
import { getQuestionPool, CONTENT_VERSION } from './data/questionPool';
import { generateExam, generateWeakExam } from './engine/examGenerator';
import { scoreExam } from './engine/scorer';
import ExamHome from './components/ExamHome';
import ExamRunner from './components/ExamRunner';
import ResultsScreen from './components/ResultsScreen';
import ReviewMode from './components/ReviewMode';

/**
 * Exam Pressure Simulator root.
 * Manages question pool, active exam state, and exam history.
 */
export default function ExamSimApp() {
  const [questions, setQuestions] = useLocalStorage('examsim', 'questions', []);
  const [exams, setExams] = useLocalStorage('examsim', 'exams', []);
  const navigate = useNavigate();

  // Auto-merge: seeds on first load AND adds new questions from code updates
  useEffect(() => {
    autoMergeContent({
      namespace: 'examsim',
      key: 'questions',
      seedData: getQuestionPool(),
      seedVersion: CONTENT_VERSION,
      currentData: questions,
      setData: setQuestions,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStartExam = useCallback((config) => {
    let exam;
    if (config.type === 'weak_spots') {
      exam = generateWeakExam(questions, exams, config.questionCount);
    } else {
      exam = generateExam(questions, config);
    }

    // Store the active exam temporarily
    storageService.set('examsim', 'active-exam', exam);
    navigate(`/exam/active/${exam.id}`);
  }, [questions, exams, navigate]);

  const handleSubmitExam = useCallback((examId, submissionData) => {
    const activeExam = storageService.get('examsim', 'active-exam');
    if (!activeExam) return;

    const examQuestions = activeExam.questions
      .map(qId => questions.find(q => q.id === qId))
      .filter(Boolean);

    const score = scoreExam(examQuestions, submissionData.answers, submissionData.timePerQuestion);

    const record = {
      id: activeExam.id,
      type: activeExam.type,
      started_at: activeExam.started_at,
      completed_at: submissionData.completed_at,
      time_limit_minutes: activeExam.time_limit_minutes,
      questions: activeExam.questions,
      answers: submissionData.answers,
      flags: submissionData.flags,
      time_per_question: submissionData.timePerQuestion,
      score,
    };

    setExams(prev => [...prev, record]);
    storageService.remove('examsim', 'active-exam');

    // Update per-question stats
    setQuestions(prev => prev.map(q => {
      if (!activeExam.questions.includes(q.id)) return q;
      const pq = score.per_question?.[q.id];
      if (!pq) return q;
      return {
        ...q,
        stats: {
          times_seen: q.stats.times_seen + 1,
          times_correct: q.stats.times_correct + (pq.correct ? 1 : 0),
          avg_time_seconds: q.stats.times_seen > 0
            ? Math.round((q.stats.avg_time_seconds * q.stats.times_seen + pq.timeSpent) / (q.stats.times_seen + 1))
            : Math.round(pq.timeSpent),
        },
      };
    }));

    navigate(`/exam/results/${record.id}`);
  }, [questions, setQuestions, setExams, navigate]);

  return (
    <Routes>
      <Route path="/" element={
        <ExamHome
          questionCount={questions.length}
          pastExams={exams}
          onStartExam={handleStartExam}
        />
      } />
      <Route path="/active/:examId" element={
        <ActiveExamRoute questions={questions} onSubmit={handleSubmitExam} />
      } />
      <Route path="/results/:examId" element={
        <ResultsRoute exams={exams} questions={questions} />
      } />
      <Route path="/review/:examId" element={
        <ReviewRoute exams={exams} questions={questions} />
      } />
    </Routes>
  );
}

function ActiveExamRoute({ questions, onSubmit }) {
  const { examId } = useParams();
  const activeExam = storageService.get('examsim', 'active-exam');

  const examQuestions = useMemo(() => {
    if (!activeExam) return [];
    return activeExam.questions
      .map(qId => questions.find(q => q.id === qId))
      .filter(Boolean);
  }, [activeExam, questions]);

  if (!activeExam || examQuestions.length === 0) {
    return <div className="p-8 text-center text-gray-500">Exam not found. <a href="/exam" className="text-blue-600">Go back</a></div>;
  }

  return (
    <ExamRunner
      exam={activeExam}
      questions={examQuestions}
      onSubmit={(data) => onSubmit(examId, data)}
    />
  );
}

function ResultsRoute({ exams, questions }) {
  const { examId } = useParams();
  const examRecord = exams.find(e => e.id === examId);
  return <ResultsScreen examRecord={examRecord} questions={questions} />;
}

function ReviewRoute({ exams, questions }) {
  const { examId } = useParams();
  const examRecord = exams.find(e => e.id === examId);
  return <ReviewMode examRecord={examRecord} questions={questions} />;
}
